import { useEffect, useState } from 'react'
import { Activity, AlertCircle, ArrowRight, ShieldCheck } from 'lucide-react'
import { Navigate, useSearchParams } from 'react-router-dom'
import { KNOKA_LOGIN_URL } from '~/api/auth'
import { Button } from '~/components/ui/button'
import { useAuthStore } from '~/stores/auth-store'

// 좌측 패널에 떠다니는 노드 점(미세한 테크 모티프). delay 로 비동기 pulse.
const NODES = [
    { top: '16%', left: '24%', delay: '0s' },
    { top: '30%', left: '74%', delay: '1.4s' },
    { top: '58%', left: '16%', delay: '0.7s' },
    { top: '70%', left: '66%', delay: '2.1s' },
    { top: '44%', left: '46%', delay: '1.1s' },
] as const

// 초 단위로 현재 시각을 갱신하는 라이브 클럭(관제 tick).
function useClock() {
    const [now, setNow] = useState(() => new Date())
    useEffect(() => {
        const id = setInterval(() => setNow(new Date()), 1000)
        return () => clearInterval(id)
    }, [])
    return now
}

// 운영자 콘솔 로그인 — knoka SSO 전용. 자체 이메일/비밀번호 로그인은 없다.
// 로그인 버튼은 서버사이드 OIDC 흐름(/api/auth/login)으로 전체 페이지를 이동시킨다.
// 디자인: 콘솔 본문(라이트)과 분리된 다크 "관제 플레인" 스플릿 스크린(로그인 한정 다크 고정).
export function LoginPage() {
    const [searchParams] = useSearchParams()
    const status = useAuthStore((s) => s.status)
    const now = useClock()

    // knoka 콜백이 인가 실패로 콘솔에 되돌린 경우 안내한다(?auth_error=forbidden).
    const authError = searchParams.get('auth_error')
    const forbidden = authError === 'forbidden'

    // 이미 로그인돼 있으면 콘솔로 보낸다.
    if (status === 'authenticated') return <Navigate to='/' replace />

    const onKnokaLogin = () => {
        // 서버사이드 OIDC 흐름이라 전체 페이지를 이동한다(SPA base가 /admin이라 절대경로).
        window.location.href = KNOKA_LOGIN_URL
    }

    const clock = now.toLocaleTimeString('ko-KR', { hour12: false })
    const day = now.toLocaleDateString('ko-KR', {
        month: 'long',
        day: 'numeric',
        weekday: 'short',
    })

    return (
        <div className='dark relative flex min-h-screen w-full flex-col overflow-hidden bg-[#0a0b0f] text-zinc-100 lg:grid lg:grid-cols-[1.15fr_minmax(440px,0.85fr)]'>
            {/* ── 좌측: 몰입 관제 패널(다크 + 애니메이션) ─────────────────────── */}
            <aside className='relative flex min-h-[44vh] flex-col justify-between overflow-hidden border-b border-white/[0.06] p-8 sm:p-10 lg:min-h-screen lg:border-r lg:border-b-0 lg:p-14'>
                {/* 레이어드 애니메이션 배경 */}
                <div className='lgn-aurora' aria-hidden='true' />
                <div className='lgn-grid' aria-hidden='true' />
                <div
                    className='pointer-events-none absolute inset-0'
                    aria-hidden='true'>
                    {NODES.map((n, i) => (
                        <span
                            key={i}
                            className='lgn-node'
                            style={{
                                top: n.top,
                                left: n.left,
                                animationDelay: n.delay,
                            }}
                        />
                    ))}
                </div>
                {/* 하단 그라데이션 비네팅으로 본문과 부드럽게 합쳐짐 */}
                <div
                    className='pointer-events-none absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-b from-transparent to-[#0a0b0f]'
                    aria-hidden='true'
                />

                {/* 브랜드 */}
                <div className='relative flex items-center gap-3'>
                    <div className='flex size-11 items-center justify-center rounded-xl border border-white/10 bg-white/[0.06] backdrop-blur'>
                        <img
                            src='/admin/brand/logo-white.svg'
                            alt=''
                            className='size-7'
                        />
                    </div>
                    <div className='leading-tight'>
                        <div className='text-sm font-semibold tracking-tight text-white'>
                            HACCP AI
                        </div>
                        <div className='text-[11px] font-medium tracking-[0.18em] text-cyan-300/70 uppercase'>
                            Command Console
                        </div>
                    </div>
                </div>

                {/* 헤드라인 + 태그라인 */}
                <div className='relative max-w-xl'>
                    <div className='mb-6 inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.04] px-4 py-1.5 text-[13px] font-medium text-zinc-300'>
                        <ShieldCheck className='size-4 text-emerald-400' />
                        운영자 전용 보안 영역
                    </div>
                    <h1 className='text-4xl leading-[1.1] font-bold tracking-tight text-white sm:text-5xl lg:text-6xl'>
                        HACCP AI
                        <br />
                        관리자 콘솔
                    </h1>
                    <p className='mt-6 text-base leading-relaxed text-zinc-400 sm:text-lg'>
                        운영 관제 · AI 거버넌스 · 콘텐츠 관리.
                        <br />
                        플랫폼 전반을 한곳에서 통제하는 통합 관제 플레인입니다.
                    </p>
                </div>

                {/* 라이브 관제 상태 */}
                <div className='relative space-y-4'>
                    <div className='flex flex-wrap items-center gap-x-5 gap-y-2'>
                        <span className='inline-flex items-center gap-2 text-[13px] font-medium text-zinc-200'>
                            <span className='lgn-ping relative inline-block size-2 rounded-full bg-emerald-400' />
                            시스템 정상
                        </span>
                        <span className='inline-flex items-center gap-1.5 text-[13px] text-zinc-400 tabular-nums'>
                            <Activity className='size-3.5 text-cyan-400' />
                            {day} {clock}
                        </span>
                    </div>
                </div>
            </aside>

            {/* ── 우측: knoka SSO 로그인 패널 ──────────────────────────────── */}
            <main className='relative flex flex-1 items-center justify-center bg-[radial-gradient(60%_60%_at_72%_28%,rgba(37,99,235,0.10),transparent_70%)] p-6 sm:p-10 lg:p-12'>
                <div className='lgn-rise w-full max-w-sm'>
                    <div className='rounded-2xl border border-white/10 bg-white/[0.04] p-7 shadow-2xl backdrop-blur-xl sm:p-8'>
                        <div className='mb-6'>
                            <h2 className='text-xl font-semibold tracking-tight text-white'>
                                관리자 로그인
                            </h2>
                            <p className='mt-1.5 text-[13px] text-zinc-400'>
                                knock 계정으로 콘솔에 접근합니다.
                            </p>
                        </div>

                        {forbidden ? (
                            <div
                                role='alert'
                                className='mb-4 flex items-start gap-2 rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-[13px] text-red-300'>
                                <AlertCircle className='mt-0.5 size-4 shrink-0' />
                                <span>
                                    관리 권한이 없는 계정이에요. 운영자 권한이
                                    부여된 knock 계정으로 로그인해 주세요.
                                </span>
                            </div>
                        ) : null}

                        {/* knoka SSO 로그인 — 유일한 로그인 수단. */}
                        <Button
                            type='button'
                            size='lg'
                            onClick={onKnokaLogin}
                            className='lgn-submit h-11 w-full text-sm font-semibold text-white'>
                            knock으로 로그인
                            <ArrowRight className='size-4' />
                        </Button>

                        <div className='mt-6 flex items-center gap-3 text-[12px] text-zinc-500'>
                            <span className='h-px flex-1 bg-white/10' />
                            <span>계정 분리</span>
                            <span className='h-px flex-1 bg-white/10' />
                        </div>
                        <p className='mt-3 text-center text-[12px] leading-relaxed text-zinc-500'>
                            엔드유저는 서비스 사이트에서 로그인하세요.
                            <br />이 콘솔은 운영자 전용입니다.
                        </p>
                    </div>
                </div>
            </main>
        </div>
    )
}
